import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Plus, MagnifyingGlass, IdentificationCard, Pencil, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SantriList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kelas, setKelas] = useState("");
  const [kamar, setKamar] = useState("");
  const [halaqah, setHalaqah] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [master, setMaster] = useState({ kelas: [], kamar: [], halaqah: [] });
  const [form, setForm] = useState({ nis: "", nama: "", jenis_kelamin: "L", status: "aktif" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = { q: q || undefined, kelas_id: kelas || undefined, kamar_id: kamar || undefined, halaqah_id: halaqah || undefined };
    const { data } = await api.get("/santri", { params });
    setItems(data);
  };

  useEffect(() => {
    (async () => {
      try {
        const [k, ka, h] = await Promise.all([
          api.get("/master/kelas"),
          api.get("/master/kamar"),
          api.get("/master/halaqah"),
        ]);
        setMaster({ kelas: k.data, kamar: ka.data, halaqah: h.data });
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [q, kelas, kamar, halaqah]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/santri/${editing.id}`, form);
        toast.success("Santri diperbarui");
      } else {
        await api.post("/santri", form);
        toast.success("Santri ditambahkan");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ nis: "", nama: "", jenis_kelamin: "L", status: "aktif" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus santri ini?")) return;
    await api.delete(`/santri/${id}`);
    toast.success("Santri dihapus");
    load();
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      nis: s.nis, nama: s.nama, jenis_kelamin: s.jenis_kelamin, status: s.status,
      kelas_id: s.kelas_id, kamar_id: s.kamar_id, halaqah_id: s.halaqah_id,
      alamat: s.alamat, tempat_lahir: s.tempat_lahir, tanggal_lahir: s.tanggal_lahir,
      nama_wali: s.nama_wali, no_hp_wali: s.no_hp_wali, tanggal_masuk: s.tanggal_masuk,
    });
    setShowForm(true);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Master</div>
          <h1 className="font-display text-3xl font-bold text-[#064E3B]">Data Santri</h1>
        </div>
        <button
          data-testid="santri-add-btn"
          onClick={() => {
            setEditing(null);
            setForm({ nis: "", nama: "", jenis_kelamin: "L", status: "aktif" });
            setShowForm(true);
          }}
          className="imsyaf-btn-primary"
        >
          <Plus size={16} /> Tambah Santri
        </button>
      </div>

      <div className="imsyaf-card p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-2.5 text-slate-400" size={16} />
          <input
            data-testid="santri-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari NIS / nama..."
            className="imsyaf-input pl-9"
          />
        </div>
        <select className="imsyaf-input md:w-44" value={kelas} onChange={(e) => setKelas(e.target.value)} data-testid="filter-kelas">
          <option value="">Semua Kelas</option>
          {master.kelas.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
        </select>
        <select className="imsyaf-input md:w-44" value={kamar} onChange={(e) => setKamar(e.target.value)} data-testid="filter-kamar">
          <option value="">Semua Kamar</option>
          {master.kamar.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
        </select>
        <select className="imsyaf-input md:w-48" value={halaqah} onChange={(e) => setHalaqah(e.target.value)} data-testid="filter-halaqah">
          <option value="">Semua Halaqah</option>
          {master.halaqah.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
        </select>
      </div>

      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead className="bg-[#14532d] text-white">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">NIS</th>
              <th className="text-left px-4 py-3 font-semibold">Nama</th>
              <th className="text-left px-4 py-3 font-semibold">Kelas</th>
              <th className="text-left px-4 py-3 font-semibold">Kamar</th>
              <th className="text-left px-4 py-3 font-semibold">Halaqah</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`santri-row-${s.nis}`} className="border-t border-slate-100">
                <td className="px-4 py-3 font-mono">{s.nis}</td>
                <td className="px-4 py-3 font-medium">{s.nama}</td>
                <td className="px-4 py-3">{s.kelas_nama || "—"}</td>
                <td className="px-4 py-3">{s.kamar_nama || "—"}</td>
                <td className="px-4 py-3">{s.halaqah_nama || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`imsyaf-badge ${s.status === "aktif" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}`}>{s.status}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/santri/${s.id}`} className="imsyaf-btn-ghost !py-1" data-testid={`santri-detail-${s.nis}`}>Detail</Link>
                  <Link to={`/santri/${s.id}/id-card`} className="imsyaf-btn-ghost !py-1" title="Kartu Identitas">
                    <IdentificationCard size={16} weight="duotone" />
                  </Link>
                  <button className="imsyaf-btn-ghost !py-1" title="Edit" onClick={() => openEdit(s)}>
                    <Pencil size={16} />
                  </button>
                  <button className="imsyaf-btn-ghost !py-1 text-red-600" title="Hapus" onClick={() => del(s.id)}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">Belum ada santri.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={submit} className="imsyaf-card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="santri-form">
            <h3 className="font-display text-xl font-bold text-[#064E3B] mb-4">{editing ? "Edit Santri" : "Tambah Santri"}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="text-xs">NIS *</label>
                <input required data-testid="santri-nis-input" className="imsyaf-input" value={form.nis} onChange={(e) => setForm({ ...form, nis: e.target.value })} /></div>
              <div><label className="text-xs">Nama *</label>
                <input required data-testid="santri-nama-input" className="imsyaf-input" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></div>
              <div><label className="text-xs">Jenis Kelamin</label>
                <select className="imsyaf-input" value={form.jenis_kelamin || "L"} onChange={(e) => setForm({ ...form, jenis_kelamin: e.target.value })}>
                  <option value="L">Laki-laki</option><option value="P">Perempuan</option>
                </select></div>
              <div><label className="text-xs">Status</label>
                <select className="imsyaf-input" value={form.status || "aktif"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="aktif">Aktif</option><option value="lulus">Lulus</option><option value="keluar">Keluar</option>
                </select></div>
              <div><label className="text-xs">Kelas</label>
                <select className="imsyaf-input" value={form.kelas_id || ""} onChange={(e) => setForm({ ...form, kelas_id: e.target.value })}>
                  <option value="">-</option>{master.kelas.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select></div>
              <div><label className="text-xs">Kamar</label>
                <select className="imsyaf-input" value={form.kamar_id || ""} onChange={(e) => setForm({ ...form, kamar_id: e.target.value })}>
                  <option value="">-</option>{master.kamar.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select></div>
              <div><label className="text-xs">Halaqah</label>
                <select className="imsyaf-input" value={form.halaqah_id || ""} onChange={(e) => setForm({ ...form, halaqah_id: e.target.value })}>
                  <option value="">-</option>{master.halaqah.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
                </select></div>
              <div><label className="text-xs">Tanggal Masuk</label>
                <input type="date" className="imsyaf-input" value={form.tanggal_masuk || ""} onChange={(e) => setForm({ ...form, tanggal_masuk: e.target.value })} /></div>
              <div><label className="text-xs">Tempat Lahir</label>
                <input className="imsyaf-input" value={form.tempat_lahir || ""} onChange={(e) => setForm({ ...form, tempat_lahir: e.target.value })} /></div>
              <div><label className="text-xs">Tanggal Lahir</label>
                <input type="date" className="imsyaf-input" value={form.tanggal_lahir || ""} onChange={(e) => setForm({ ...form, tanggal_lahir: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="text-xs">Alamat</label>
                <textarea rows={2} className="imsyaf-input" value={form.alamat || ""} onChange={(e) => setForm({ ...form, alamat: e.target.value })} /></div>
              <div><label className="text-xs">Nama Wali</label>
                <input className="imsyaf-input" value={form.nama_wali || ""} onChange={(e) => setForm({ ...form, nama_wali: e.target.value })} /></div>
              <div><label className="text-xs">No HP Wali</label>
                <input className="imsyaf-input" value={form.no_hp_wali || ""} onChange={(e) => setForm({ ...form, no_hp_wali: e.target.value })} /></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="imsyaf-btn-outline" onClick={() => setShowForm(false)}>Batal</button>
              <button type="submit" className="imsyaf-btn-primary" data-testid="santri-save-btn">Simpan</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
